/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getSdk } from "@cosmos/hygraph-schema/dist/generated/resolvers.hygraph";
import { dedupeRequestBody } from "@cosmos/hygraph-schema/scripts";
import { GraphQLClient } from "graphql-request";
import { GraphQLClientResponse } from "node_modules/graphql-request/build/legacy/helpers/types";

import type { RequestMiddleware, RequestOptions } from "graphql-request";

type GraphQLClientRequestHeaders = RequestOptions["requestHeaders"];
export type SdkType = ReturnType<typeof getSdk>;
export type ContentTypeNames = keyof SdkType;

const mediaUrlPrefix = `https://${process.env.HYGRAPH_MEDIA_DOMAIN || process.env.NEXT_PUBLIC_HYGRAPH_MEDIA_DOMAIN}`;
interface HygraphExtensions {
	complexity?: {
		complexityTree: {
			actual_no_of_docs: number;
			children: {
				actual_cost: number;
				actual_no_of_docs: number;
				estimated_cost: number;
				estimated_no_of_docs: number;
				field_name: string;
				xpath: string;
			}[];
			estimated_no_of_docs: number;
		};
		total_actual_cost: number;
		total_actual_docs: number;
		total_estimated_cost: number;
		total_estimated_docs: number;
	};
}

function fetchWrap(input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response> {
	return fetch(input, { ...init, cache: "no-store" });
}

/**
 * Strip duplicate fragment definitions from every outgoing query before it hits Hygraph.
 * The generated SDK's nested `FragmentDoc` concatenation can emit the same fragment more
 * than once (see @cosmos/hygraph-schema/scripts), which Hygraph rejects with
 * `There can be only one fragment named "…"`. Applied to all clients below.
 */
const dedupeFragmentsMiddleware: RequestMiddleware = (request) => {
	if (typeof request.body === "string") {
		const deduped = dedupeRequestBody(request.body);
		if (deduped !== request.body) {
			return { ...request, body: deduped };
		}
	}
	return request;
};

/** Shared GraphQLClient config for Hygraph clients (content-service, per-endpoint, preview). */
function createHygraphClient(endpoint: string, token?: string): GraphQLClient {
	const headers: Record<string, string> = {
		"gcms-media-url-prefix": mediaUrlPrefix,
	};
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	return new GraphQLClient(endpoint, {
		credentials: "include",
		mode: "cors",
		headers,
		fetch: fetchWrap,
		requestMiddleware: dedupeFragmentsMiddleware,
	});
}

const hygraph = new GraphQLClient(`${process.env.HYGRAPH_ENDPOINT}/${process.env.HYGRAPH_ENVIRONMENT}` || "", {
	credentials: "include",
	mode: "cors",
	headers: {
		Authorization: `Bearer ${process.env.HYGRAPH_TOKEN}`,
		"gcms-media-url-prefix": mediaUrlPrefix,
	},
	fetch: fetchWrap,
	requestMiddleware: dedupeFragmentsMiddleware,

	responseMiddleware: (response: GraphQLClientResponse<unknown> | Error) => {
		if (response instanceof Error) {
			console.error("[GraphQL] ERROR", response.message);
		} else if (response.data) {
			const name = response.headers.get("gcms-operation-name");
			const hit = response.headers.get("gcms-cache");

			const ext: HygraphExtensions = response.extensions as HygraphExtensions;
			if (process.env.HYGRAPH_INSPECT_COMPLEXITY === true || process.env.HYGRAPH_INSPECT_COMPLEXITY === "true") {
				console.log(
					`GRAPHQL [${name}] ${hit} request completed. ${
						ext?.complexity?.total_actual_cost ? `complexity_cost [${ext?.complexity?.total_actual_cost}]` : ""
					}`,
				);
			}
		}
	},
});

const contentServiceHygraph = createHygraphClient(process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT || "");

export interface HygraphTarget {
	endpoint: string;
	token?: string;
	/** Draft-stage endpoint for live preview; defaults to `endpoint` when only `previewToken` is set.
	 *  Ignored unless `previewToken` is also set. */
	previewEndpoint?: string;
	/** Token with DRAFT-stage read access to this site's Hygraph project (live preview).
	 *  Must NOT be the published-content token — a published token here silently serves
	 *  published content in preview mode. */
	previewToken?: string;
	ownerId?: string;
	locales?: string[];
}

/** Per-site Hygraph target registry. Populated at startup; looked up at
 *  request time via the x-cosmos-site-id header. */
const hygraphTargetRegistry = new Map<string, HygraphTarget>();

/** Token-only registry for getOrCreateClient (maps endpoint URL → token). */
const endpointTokenRegistry = new Map<string, string>();

/** Draft-stage (live preview) client config keyed by the site's published endpoint.
 *  `ownerSiteId` records which site registered the entry: on shared Hygraph projects a
 *  sub-tenant (with a draft token) and the project's default tenant (without one) share the
 *  same published endpoint, so a tokenless registration must not clobber the sub-tenant's
 *  entry under the same key (see registerHygraphTarget). */
const endpointPreviewRegistry = new Map<string, { endpoint: string; token: string; ownerSiteId: string }>();

/** Register a site's Hygraph target. Call once per site at startup; re-registering
 *  replaces the target (including adding, rotating, or removing the preview entry). */
export function registerHygraphTarget(siteId: string, target: HygraphTarget): void {
	hygraphTargetRegistry.set(siteId, target);
	if (target.token) {
		endpointTokenRegistry.set(target.endpoint, target.token);
	}
	if (target.previewToken) {
		if (target.previewToken === target.token) {
			console.warn(
				`[Hygraph] Site "${siteId}" registered identical published and preview tokens — ` +
					`live preview will silently serve published content. previewToken must be DRAFT-stage scoped.`,
			);
		}
		endpointPreviewRegistry.set(target.endpoint, {
			endpoint: target.previewEndpoint || target.endpoint,
			token: target.previewToken,
			ownerSiteId: siteId,
		});
	} else {
		// Re-registration without a preview token removes any previous draft entry,
		// so a revoked token doesn't keep serving preview until process restart.
		// BUT only remove an entry this same site registered: on a shared Hygraph project
		// the published endpoint is shared by a sub-tenant (which DOES have a draft token,
		// e.g. lore/eac/fab/support-kws) and the project's default tenant (which does NOT,
		// e.g. tm-cosmos/onlineservices/quixel/kws). Without this owner check, the tokenless
		// default tenant's registration would delete the sub-tenant's draft entry under the
		// shared key, collapsing live preview to the broken global client for every such pair.
		const existing = endpointPreviewRegistry.get(target.endpoint);
		if (existing && existing.ownerSiteId === siteId) {
			endpointPreviewRegistry.delete(target.endpoint);
		}
	}
}

/** @deprecated Use registerHygraphTarget instead. */
export function registerEndpointToken(endpoint: string, token: string): void {
	endpointTokenRegistry.set(endpoint, token);
}

/** Resolve the Hygraph target for the current request from the x-cosmos-site-id header.
 *  Returns undefined outside Server Component context. */
export async function resolveHygraphTarget(): Promise<HygraphTarget | undefined> {
	try {
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		const { headers } = require("next/headers") as { headers: () => Promise<{ get: (k: string) => string | null }> };
		const h = await headers();
		const siteId = h.get("x-cosmos-site-id");
		if (siteId) return hygraphTargetRegistry.get(siteId);
	} catch {
		// Not in Server Component context — fall through
	}
	return undefined;
}

/** Cache of per-endpoint GraphQL clients for multi-site support */
const endpointClientCache = new Map<string, GraphQLClient>();

function getOrCreateClient(endpoint: string): GraphQLClient {
	const cached = endpointClientCache.get(endpoint);
	if (cached) return cached;

	const client = createHygraphClient(endpoint, endpointTokenRegistry.get(endpoint));
	endpointClientCache.set(endpoint, client);
	return client;
}

const previewHygraph = createHygraphClient(
	`${process.env.HYGRAPH_ENDPOINT}/${process.env.HYGRAPH_ENVIRONMENT}`,
	process.env.HYGRAPH_PREVIEW_TOKEN,
);

/** Cache of draft-stage clients for multi-site live preview. Keyed by endpoint AND token
 *  defensively: registration currently happens once at module evaluation, but if a target
 *  is ever re-registered with a rotated token at runtime, the composite key guarantees a
 *  fresh client instead of 401ing on a stale one. */
const previewClientCache = new Map<string, GraphQLClient>();

function getOrCreatePreviewClient(config: { endpoint: string; token: string }): GraphQLClient {
	const cacheKey = `${config.endpoint}:${config.token}`;
	const cached = previewClientCache.get(cacheKey);
	if (cached) return cached;

	const client = createHygraphClient(config.endpoint, config.token);
	previewClientCache.set(cacheKey, client);
	return client;
}

export const isValidPreviewParam = (previewParam?: string): boolean => {
	if (previewParam && previewParam === process.env.HYGRAPH_PREVIEW_PARAM) {
		return true;
	}
	return false;
};

/**
 * Resolves the GraphQL client for a request. Resolution order:
 * 1. Valid `previewParam` + `endpoint` with a registered `previewToken` → that site's
 *    draft-stage client (multi-tenant live preview).
 * 2. Valid `previewParam` otherwise → the global env-driven preview client when
 *    `HYGRAPH_ENDPOINT` is set (single-project/standalone sites); when it is unset
 *    (multi-tenant deployments) the global client would be an invalid URL, so degrade to
 *    the site's published client (preview shows published content rather than 500ing).
 * 3. `endpoint` → the per-endpoint published client (token from registerHygraphTarget).
 * 4. `HYGRAPH_CONTENT_SERVICE_ENDPOINT` → the content-service client; else the
 *    global env-driven published client.
 */
export const getHygraph = (previewParam?: string, endpoint?: string): GraphQLClient => {
	if (isValidPreviewParam(previewParam)) {
		console.warn("Using Hygraph PREVIEW");
		// Multi-tenant apps register a per-site draft-stage target (previewToken on
		// registerHygraphTarget); prefer it over the global env-driven preview client,
		// which can only ever point at a single project.
		const sitePreview = endpoint ? endpointPreviewRegistry.get(endpoint) : undefined;
		if (sitePreview) {
			return getOrCreatePreviewClient(sitePreview);
		}
		// No per-site draft client. The global previewHygraph is only usable when
		// HYGRAPH_ENDPOINT is configured; multi-tenant deployments leave it unset, so it would
		// be built as an invalid `undefined/undefined` URL and throw at request time. In that
		// case fall back to the site's published client so preview degrades to published
		// content instead of a 500, rather than returning a structurally-broken client.
		if (process.env.HYGRAPH_ENDPOINT) {
			return previewHygraph;
		}
		if (endpoint) {
			return getOrCreateClient(endpoint);
		}
	}
	if (endpoint) {
		return getOrCreateClient(endpoint);
	}
	if (process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT) {
		return contentServiceHygraph;
	}

	return hygraph;
};

export const getHygraphHeaders = (preview: string | undefined, endpoint?: string): GraphQLClientRequestHeaders => {
	const headers: GraphQLClientRequestHeaders | undefined = {};
	if (preview || endpoint || process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT) {
		headers["hyg-stale-while-revalidate"] = "0";
	} else {
		if (process.env.HYGRAPH_STALE_REVALIDATE) {
			headers["hyg-stale-while-revalidate"] = `${process.env.HYGRAPH_STALE_REVALIDATE}`;
		}
	}
	return headers;
};

export const getHygraphCosmosPageData = async <FunctionName extends ContentTypeNames>(
	contentType: FunctionName,
	payload: Parameters<SdkType[FunctionName]>[0],
	preview: string | undefined,
	endpoint?: string,
): Promise<ReturnType<SdkType[FunctionName]>> => {
	const resolvedEndpoint = endpoint || (await resolveHygraphTarget())?.endpoint;
	const headers: GraphQLClientRequestHeaders | undefined = getHygraphHeaders(preview, resolvedEndpoint);

	if (!payload) {
		throw new Error("Missing payload");
	}

	const inspectComplexity =
		process.env.HYGRAPH_INSPECT_COMPLEXITY === true || process.env.HYGRAPH_INSPECT_COMPLEXITY === "true";
	const sdk = getSdk(getHygraph(preview, resolvedEndpoint));

	const fn = sdk[contentType];
	const start = Date.now();
	const pageData = await fn(payload as any, {
		"x-inspect-complexity": inspectComplexity ? "true" : "false",
		...headers,
	});

	if (inspectComplexity) {
		console.log(`GRAPHQL TIMING [${contentType}] ${Date.now() - start}`);
	}

	return pageData as ReturnType<SdkType[FunctionName]>;
};

/**
 * Generic function to get Hygraph page data for any SDK type.
 * This eliminates code duplication across all hygraph-*.ts files.
 *
 * @param getSdk - The getSdk function from the specific schema package
 * @param contentType - The content type/function name to call
 * @param payload - The payload/parameters for the function
 * @param preview - Preview parameter for Hygraph
 * @returns Promise with the page data
 */
export const getHygraphPageData = async <
	TSdk extends Record<string, (...args: any[]) => Promise<any>>,
	FunctionName extends keyof TSdk,
>(
	getSdk: (client: GraphQLClient) => TSdk,
	contentType: FunctionName,
	payload: Parameters<TSdk[FunctionName]>[0],
	preview: string | undefined,
	endpoint?: string,
): Promise<Awaited<ReturnType<TSdk[FunctionName]>>> => {
	const resolvedEndpoint = endpoint || (await resolveHygraphTarget())?.endpoint;
	const headers: GraphQLClientRequestHeaders | undefined = getHygraphHeaders(preview, resolvedEndpoint);

	if (!payload) {
		throw new Error("Missing payload");
	}

	const inspectComplexity =
		process.env.HYGRAPH_INSPECT_COMPLEXITY === true || process.env.HYGRAPH_INSPECT_COMPLEXITY === "true";
	const sdk = getSdk(getHygraph(preview, resolvedEndpoint));

	const fn = sdk[contentType];
	const start = Date.now();
	const pageData = await fn(payload as any, {
		"x-inspect-complexity": inspectComplexity ? "true" : "false",
		...headers,
	});

	if (inspectComplexity) {
		console.log(`GRAPHQL TIMING [${String(contentType)}] ${Date.now() - start}`);
	}

	return pageData;
};
